import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/lib/data";
import { subscribeNewsletter } from "@/lib/api";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.525 0c1.33 2.39 3.71 4.045 6.475 4.32v3.265c-1.7-.08-3.305-.61-4.78-1.5v7.92c0 5.1-3.93 7.99-7.81 7.99-2.31 0-4.43-1.13-5.65-3.04-2.04-3.2-.74-7.65 2.71-9.27 1.4-.66 3-.79 4.51-.42v3.46c-.83-.27-1.78-.21-2.55.19-2.06 1.07-1.74 4.36.6 4.93 1.75.42 3.66-.96 3.66-2.99V0h2.83z" />
  </svg>
);

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(
        res.already_subscribed
          ? "You're already on the list, thanks!"
          : "You're in. We'll bring the soul to your inbox.",
      );
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0c0c0c] border-t border-[#F5ECD7]/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <h3 className="font-display text-4xl md:text-5xl text-[#F5ECD7] tracking-tighter leading-[1.05]">
              Get the <span className="text-[#C99C3A]">drop</span>.
              <br />
              <span className="font-serif-italic font-normal text-[#F5ECD7]/80">
                Schedules, festivals, new menu items.
              </span>
            </h3>
            <form onSubmit={onSubscribe} className="mt-8 max-w-md flex items-end gap-3">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-line flex-1"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={loading}
                className="btn-gold !py-3 !px-5 !text-[11px] shrink-0"
              >
                {loading ? "..." : "Subscribe"} <ArrowRight size={14} />
              </button>
            </form>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C99C3A] mb-5">Explore</p>
            <ul className="space-y-3 text-[#F5ECD7]/75 text-sm">
              <li><Link to="/menu" className="hover:text-[#C99C3A]">Menu</Link></li>
              <li><Link to="/story" className="hover:text-[#C99C3A]">Our Story</Link></li>
              <li><Link to="/gallery" className="hover:text-[#C99C3A]">Gallery</Link></li>
              <li><Link to="/schedule" className="hover:text-[#C99C3A]">Schedule</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C99C3A] mb-5">Work With Us</p>
            <ul className="space-y-3 text-[#F5ECD7]/75 text-sm">
              <li><Link to="/catering" className="hover:text-[#C99C3A]">Catering</Link></li>
              <li><Link to="/book" className="hover:text-[#C99C3A]">Book the Truck</Link></li>
              <li><Link to="/franchise" className="hover:text-[#C99C3A]">Franchise</Link></li>
              <li><Link to="/shop" className="hover:text-[#C99C3A]">Frozen Shop</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C99C3A] mb-5">Reach Out</p>
            <p className="text-[#F5ECD7]/85 text-sm leading-relaxed">
              {CONTACT.email}<br />
              {CONTACT.city}<br />
              <span className="text-[#F5ECD7]/50">Open kitchen, open community.</span>
            </p>
            <div className="flex gap-4 mt-5 text-[#F5ECD7]/70">
              <a href={CONTACT.instagram} aria-label="Instagram" className="hover:text-[#C99C3A]" data-testid="social-instagram"><Instagram size={20} /></a>
              <a href={CONTACT.facebook} aria-label="Facebook" className="hover:text-[#C99C3A]" data-testid="social-facebook"><Facebook size={20} /></a>
              <a href={CONTACT.tiktok} aria-label="TikTok" className="hover:text-[#C99C3A]" data-testid="social-tiktok"><TikTokIcon className="w-5 h-5" /></a>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-[#F5ECD7]/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#F5ECD7]/45">
          <p>© {new Date().getFullYear()} Hashtag Taco Soul. Where Tacos Meet Soul.</p>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-[#C99C3A]">Privacy</Link>
            <Link to="/careers" className="hover:text-[#C99C3A]">Careers</Link>
            <Link to="/contact" className="hover:text-[#C99C3A]">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
